import yaml from 'js-yaml';

export const FIELD_VIEW_OPTIONS = {
  input: [
    { id: 'html', label: 'HTML', readOnly: true },
    { id: 'text', label: 'Text', readOnly: false },
    { id: 'yaml', label: 'YAML', readOnly: false },
    { id: 'json', label: 'JSON', readOnly: false },
    { id: 'pretty', label: 'Pretty', readOnly: true },
    { id: 'tree', label: 'Tree', readOnly: true },
  ],
  expected: [
    { id: 'html', label: 'HTML', readOnly: true },
    { id: 'text', label: 'Text', readOnly: false },
    { id: 'yaml', label: 'YAML', readOnly: false },
    { id: 'json', label: 'JSON', readOnly: false },
    { id: 'pretty', label: 'Pretty', readOnly: true },
    { id: 'tree', label: 'Tree', readOnly: true },
  ],
  metadata: [
    { id: 'yaml', label: 'YAML', readOnly: false },
    { id: 'json', label: 'JSON', readOnly: false },
    { id: 'pretty', label: 'Pretty', readOnly: true },
    { id: 'tree', label: 'Tree', readOnly: true },
  ],
};

export function defaultViewForField(fieldKind) {
  return fieldKind === 'metadata' ? 'yaml' : 'yaml';
}

export function isViewReadOnly(viewType, fieldKind) {
  const opt = FIELD_VIEW_OPTIONS[fieldKind]?.find((o) => o.id === viewType);
  return opt?.readOnly ?? true;
}

export function toYamlLike(value) {
  if (value == null || value === '') return 'null';
  if (typeof value === 'string') {
    const t = value.trim();
    if (!t) return 'null';
    if (!t.includes('\n') && t.length < 120) return t;
    return `|\n${t.split('\n').map((l) => `  ${l}`).join('\n')}`;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return yaml.dump(value, { lineWidth: 120, noRefs: true }).trimEnd();
  } catch {
    return String(value);
  }
}

export function parseYamlField(text) {
  const trimmed = String(text || '').trim();
  if (!trimmed || trimmed === 'null') return null;
  return yaml.load(trimmed);
}

export function parseJsonField(text) {
  const trimmed = String(text || '').trim();
  if (!trimmed || trimmed === 'null') return null;
  return JSON.parse(trimmed);
}

function coerceStoredExpected(parsed) {
  if (parsed == null) return '';
  if (typeof parsed === 'string') return parsed;
  return JSON.stringify(parsed);
}

function parseLooseObject(text) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return {};
  try {
    const fromJson = parseJsonField(trimmed);
    if (fromJson != null && typeof fromJson === 'object' && !Array.isArray(fromJson)) return fromJson;
  } catch { /* fall through */ }
  try {
    const fromYaml = parseYamlField(trimmed);
    if (fromYaml != null && typeof fromYaml === 'object' && !Array.isArray(fromYaml)) return fromYaml;
  } catch { /* fall through */ }
  return { value: trimmed };
}

export function valueToTree(value, depth = 0) {
  const pad = '  '.repeat(depth);
  if (value === null) return `${pad}null`;
  if (value === undefined) return `${pad}undefined`;
  if (Array.isArray(value)) {
    if (!value.length) return `${pad}[]`;
    return value.map((item, i) => {
      if (item != null && typeof item === 'object') {
        return `${pad}[${i}]:\n${valueToTree(item, depth + 1)}`;
      }
      return `${pad}[${i}]: ${String(item)}`;
    }).join('\n');
  }
  if (typeof value === 'object') {
    const keys = Object.keys(value);
    if (!keys.length) return `${pad}{}`;
    return keys.map((key) => {
      const v = value[key];
      if (v != null && typeof v === 'object') {
        return `${pad}${key}:\n${valueToTree(v, depth + 1)}`;
      }
      return `${pad}${key}: ${v === null ? 'null' : String(v)}`;
    }).join('\n');
  }
  return `${pad}${String(value)}`;
}

export function valueToPretty(value) {
  if (value == null || value === '') return 'null';
  if (typeof value === 'string') {
    const t = value.trim();
    if (!t) return 'null';
    try {
      return JSON.stringify(JSON.parse(t), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function looksLikeHtml(value) {
  if (typeof value !== 'string') return false;
  const t = value.trim();
  return /^<!DOCTYPE html/i.test(t) || /^<html[\s>]/i.test(t) || /^<([a-z][\w-]*)(\s[^>]*)?>[\s\S]*<\/\1>$/i.test(t);
}

export function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function valueToHtmlDocument(value) {
  if (value == null || value === '') {
    return '<p class="ds-html-empty">null</p>';
  }
  if (typeof value === 'string' && looksLikeHtml(value)) {
    return value;
  }
  const pretty = typeof value === 'string' ? value : valueToPretty(value);
  return `<pre class="ds-html-pre">${escapeHtml(pretty)}</pre>`;
}

export function parseValueForField(text, viewType, fieldKind) {
  const trimmed = String(text || '').trim();

  if (fieldKind === 'expected') {
    if (viewType === 'text') return coerceStoredExpected(text);
    if (viewType === 'yaml' || viewType === 'json') {
      const parsed = viewType === 'yaml' ? parseYamlField(text) : parseJsonField(text);
      return coerceStoredExpected(parsed);
    }
    return coerceStoredExpected(text);
  }

  if (viewType === 'text') {
    if (!trimmed || trimmed === 'null') return {};
    return parseLooseObject(text);
  }
  if (viewType === 'yaml') {
    const parsed = parseYamlField(text);
    if (parsed != null && (typeof parsed !== 'object' || Array.isArray(parsed))) {
      throw new Error(`${fieldKind === 'metadata' ? 'Metadata' : 'Input'} must be an object.`);
    }
    return parsed || {};
  }
  if (viewType === 'json') {
    const parsed = parseJsonField(text);
    if (parsed != null && (typeof parsed !== 'object' || Array.isArray(parsed))) {
      throw new Error(`${fieldKind === 'metadata' ? 'Metadata' : 'Input'} must be an object.`);
    }
    return parsed || {};
  }
  return parseLooseObject(text);
}

export function storedExpectedToValue(expected) {
  if (expected == null || expected === '') return null;
  if (typeof expected !== 'string') return expected;
  const t = expected.trim();
  if (!t) return null;
  if (t.startsWith('{') || t.startsWith('[')) {
    try { return JSON.parse(t); } catch { /* keep string */ }
  }
  return expected;
}

export function valueToDisplayText(value, viewType, fieldKind) {
  const v = fieldKind === 'expected' ? storedExpectedToValue(value) : value;

  switch (viewType) {
    case 'text':
      if (v == null) return '';
      if (typeof v === 'string') return v;
      return toYamlLike(v);
    case 'yaml':
      return toYamlLike(v);
    case 'json':
      if (v == null) return 'null';
      if (typeof v === 'string') {
        try { return JSON.stringify(JSON.parse(v), null, 2); } catch { return v; }
      }
      return JSON.stringify(v, null, 2);
    case 'pretty':
      return valueToPretty(v);
    case 'tree':
      return valueToTree(v);
    case 'html':
      return typeof v === 'string' ? v : valueToPretty(v);
    default:
      return toYamlLike(v);
  }
}

export function reformatDisplayText(text, viewType) {
  if (viewType === 'yaml') {
    const parsed = parseYamlField(text);
    return toYamlLike(parsed);
  }
  if (viewType === 'json') {
    const parsed = parseJsonField(text);
    return JSON.stringify(parsed, null, 2);
  }
  return text;
}
